import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { IPaginatedResponse, IService } from "maps-angular-components";

import { DatePipe } from "@angular/common";
import { Injectable } from '@angular/core';
import { FormGroup } from "@angular/forms";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { Funcao } from "../funcao/models/funcao";
import { handleError } from "../shared/util/error-util";
import { CreateRegra } from "./create-regra";
import { Regra } from "./regra";
import { RegraSelecaoModel } from "./selecao-regras/regra-selecao-model";

@Injectable({
  providedIn: 'root'
})
export class RegraService implements IService<Regra> {

  url = '/v1/regras';
  private readonly dateFormat = 'yyyy-MM-dd';
  private readonly _httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});

  constructor(private readonly httpclient: HttpClient,
              private readonly datePipe: DatePipe) {
  }

  gravarSimples(regra: Regra): Observable<any> {
    const headers = this._httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = {headers, params};
    return this.httpclient.post(`${this.url}/simples`, regra, options).pipe();
  }

  alterarRegraSimples(regra: Regra): Observable<any> {
    const headers = this._httpHeaders;
    const options = {headers};
    return this.httpclient.put(`${this.url}/simples`, regra, options).pipe(catchError(handleError));
  }

  gravarComposta(regra: Regra): Observable<any> {
    const headers = this._httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = {headers, params};
    return this.httpclient.post(`${this.url}/compostas`, regra, options).pipe();
  }

  alterarRegraComposta(regra: Regra): Observable<any> {
    const headers = this._httpHeaders;
    const options = {headers};
    return this.httpclient.put(`${this.url}/compostas`, regra, options).pipe(catchError(handleError));
  }

  montarRegra(form: FormGroup, regrasAssociadas?: Regra[], funcaoSelecionada?: Funcao): Regra {
    const regra = new Regra();

    regra.funcao = funcaoSelecionada.nome;
    regra.nome = form.get("nome").value.toUpperCase();
    regra.parametros = {};
    regra.utilizaCarteiraExplodida = form.get('utilizaCarteiraExplodida').value;
    regra.inicioVigencia = this.datePipe.transform(form.get("dataInicioVigencia").value, this.dateFormat);
    if (form.get("dataFimVigencia").value) {
      regra.fimVigencia = this.datePipe.transform(form.get("dataFimVigencia").value, this.dateFormat);
    }
    const paramControls = form.get("parametros") as FormGroup;
    funcaoSelecionada.parametros.forEach((param) => {
      const control = paramControls.get(param.variavel);
      const group = control.value;
      let fields = {};
      if (param.tipo === 'COMPOSTO') {
        param.parametros.forEach(paramComposto => {
          // parametro composto possui um formGroup a mais.
          fields[paramComposto.variavel] = group[paramComposto.variavel];
        });

      } else if (group[param.variavel] !== undefined) {
        fields = group[param.variavel];
      } else {
        fields = group;
      }

      regra.parametros[param.variavel] = fields;

    });
    if (regrasAssociadas) {
      regra.regrasAssociadas = regrasAssociadas;
    }
    return regra;
  }

  autocomplete(val: string, endpoint: string): Observable<any> {
    const headers = this._httpHeaders;
    const options = {headers};

    return this.httpclient.get<any>(`${this.url}/${endpoint}?regra=${encodeURIComponent(val)}`, options).pipe(catchError(handleError));
  }

  removerRegra(s: any, regras: Regra[]) {
    const index = regras.findIndex(i => i.id === s.id);
    if (index >= 0) {
      return regras.splice(index, 1);
    }
    return regras;
  }

  getRegras(page: any, regras: Regra[]) {
    const regrasByPage: Regra[] = [];
    const start = (page * 10) - 10;
    for (let i = start; i < (page * 10) && i < regras.length; i++) {
      regrasByPage.push(regras[i]);
    }
    return regrasByPage;
  }

  getRegrasTotalPage(regras: Regra[]) {
    const number = Math.ceil(regras.length / 10);
    if (number === 0) {
      return 1;
    }
    return number;
  }

  addRegra(value: Regra, regras: Regra[]) {
    if (!value || (value.nome !== null && regras.includes(value))) {
      return false;
    }
    regras.push(value);
    regras.sort(this.compareRegras);
    return true;
  }

  findById(id: number): Observable<Regra> {
    const headers = this._httpHeaders;
    return this.httpclient.get<Regra>(`${this.url}/${id}`, {headers}).pipe(catchError(handleError));
  }

  search(model: CreateRegra, pageSize: number, composta: boolean): Observable<any> {
    if (composta)
      return this.searchComposta(model, pageSize);
    return this.searchSimples(model, pageSize);
  }

  searchSimples(model: CreateRegra, pageSize: number): Observable<any> {
    const headers = this._httpHeaders;

    let params = this.createFormParam(model);

    params = params.set('size', pageSize.toString());
    const options = {headers, params};
    return this.httpclient.get<any[]>(`${this.url}/simples`, options).pipe(catchError(handleError));
  }

  searchComposta(model: CreateRegra, pageSize: number): Observable<any> {
    const headers = this._httpHeaders;
    let params = this.createFormParam(model);

    params = params.set('size', pageSize.toString());

    const options = {headers, params};
    return this.httpclient.get<any[]>(`${this.url}/compostas`, options).pipe(catchError(handleError));
  }

  private createFormParam(model: any) {
    let params: HttpParams = new HttpParams();
    Object.keys(model).forEach(item => {
      if (this.notNullNotEmpty(model[item])) params = params.set(item, model[item])
    });

    if (model.inicioVigencia) {
      params = params.set('inicioVigencia', this.datePipe.transform(model.inicioVigencia, this.dateFormat));
    }

    if (model.fimVigencia) {
      params = params.set('fimVigencia', this.datePipe.transform(model.fimVigencia, this.dateFormat));
    }

    params = params.set('sort', 'funcao,nome,ASC');

    return params;

  }

  getDetalhesRegra(regra: Regra): Observable<any> {
    const headers = this._httpHeaders;
    const options = {headers};
    return this.httpclient.get<any>(`${this.url}/${regra.id}`, options).pipe();
  }


  excluirRegra(id: string, isComposta): Observable<any> {
    const headers = this._httpHeaders;
    const options = {headers};

    if (isComposta)
      return this.httpclient.delete(`${this.url}/compostas/${id}`, options);
    else
      return this.httpclient.delete(`${this.url}/simples/${id}`, options);
  }

  notNullNotEmpty(value: any): boolean {
    return value !== null && value !== "" && value !== undefined;
  }

  compareRegras(a: RegraSelecaoModel, b: RegraSelecaoModel) {
    if (a.nome < b.nome) {
      return -1;
    }
    if (a.nome > b.nome) {
      return 1;
    }
    return 0;
  }

  transformDate(date: string) {
    return new Date(date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3").concat('T00:00:00'));
  }

  parsePaginatedResponse(response): IPaginatedResponse<Regra> {
    return {
      total: null,
      data: response.map(item => new Regra({id: item.id, nome: item.name}))
    };
  }

  exportPdf(value: any, composta: boolean): Observable<HttpResponse<Blob>> {
    if (composta)
      return this.exportPdfCompostas(value);
    return this.exportPdfSimples(value);
  }

  exportXlsx(value: any, composta: boolean): Observable<HttpResponse<Blob>> {
    if (composta)
      return this.exportXlsxCompostas(value);
    return this.exportXlsxSimples(value);
  }

  exportPdfCompostas(value: any): Observable<HttpResponse<Blob>> {
    const headers = this._httpHeaders;
    const params = this.createFormParam(value).set("size", '99999999');
    return this.httpclient.get(`${this.url}/compostas/export/pdf`, {
      headers,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  exportXlsxCompostas(value: any): Observable<HttpResponse<Blob>> {
    const headers = this._httpHeaders;
    const params = this.createFormParam(value).set("size", '99999999');
    return this.httpclient.get(`${this.url}/compostas/export/xlsx`, {
      headers,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  exportPdfSimples(value: any): Observable<HttpResponse<Blob>> {
    const headers = this._httpHeaders;
    const params = this.createFormParam(value).set("size", '99999999');
    return this.httpclient.get(`${this.url}/simples/export/pdf`, {
      headers,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  exportXlsxSimples(value: any): Observable<HttpResponse<Blob>> {
    const headers = this._httpHeaders;
    const params = this.createFormParam(value).set("size", '99999999');
    return this.httpclient.get(`${this.url}/simples/export/xlsx`, {
      headers,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  downloadModelo(): Observable<HttpResponse<Blob>> {
    return this.getExportDataFrom(`${this.url}/download-modelo`);
  }

  private getExportDataFrom(path: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams();
    return this.httpclient.get(path, {
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

}
